/**
 * Adapter de almacenamiento para Web
 *
 * Implementa StoragePort usando localStorage/sessionStorage del navegador.
 */

import type { StoragePort, SecureStoragePort } from '@pepi/core'

/**
 * Implementación de StoragePort usando localStorage
 */
export class WebStorageAdapter implements StoragePort {
  private storage: Storage

  constructor(useSessionStorage = false) {
    this.storage = useSessionStorage ? sessionStorage : localStorage
  }

  async get<T = string>(key: string): Promise<T | null> {
    const value = this.storage.getItem(key)
    if (value === null) return null

    try {
      return JSON.parse(value) as T
    } catch {
      // Si no es JSON válido, retornar como string
      return value as unknown as T
    }
  }

  async set<T = string>(key: string, value: T): Promise<void> {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value)
    this.storage.setItem(key, serialized)
  }

  async remove(key: string): Promise<void> {
    this.storage.removeItem(key)
  }

  async clear(): Promise<void> {
    this.storage.clear()
  }

  async keys(): Promise<string[]> {
    return Object.keys(this.storage)
  }
}

/**
 * Implementación de SecureStoragePort para Web
 *
 * Nota: En web no hay almacenamiento nativamente seguro como en móvil.
 * Esta implementación usa localStorage con codificación base64 como medida básica.
 * Para producción, considerar cifrado adicional o soluciones como Web Crypto API.
 */
export class WebSecureStorageAdapter implements SecureStoragePort {
  private prefix = 'secure_'

  async setSecure(key: string, value: string): Promise<void> {
    // Codificación básica - en producción usar Web Crypto API
    const encoded = btoa(encodeURIComponent(value))
    localStorage.setItem(this.prefix + key, encoded)
  }

  async getSecure(key: string): Promise<string | null> {
    const encoded = localStorage.getItem(this.prefix + key)
    if (!encoded) return null

    try {
      return decodeURIComponent(atob(encoded))
    } catch {
      return null
    }
  }

  async removeSecure(key: string): Promise<void> {
    localStorage.removeItem(this.prefix + key)
  }

  async clearSecure(): Promise<void> {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.prefix))
    keys.forEach(k => localStorage.removeItem(k))
  }
}

/**
 * Crea instancias de los adapters de storage para web
 */
export function createWebStorageAdapters() {
  return {
    storage: new WebStorageAdapter(),
    secureStorage: new WebSecureStorageAdapter()
  }
}
